/**
 * UserformTaskPanel.jsx — Panel "Việc cần làm" (MUI 7)
 *
 * Hiển thị danh sách flowpending đang chờ user nhập liệu.
 * Đặt trong Drawer của FlowEditor hoặc bất kỳ layout nào.
 *
 * Dữ liệu:
 *   asyncGetList(token, "flowpending", { condition: {...} }) → load tasks
 *   asyncPostList(token, "flowpending", { _id, status, result }) → update (có _id = update)
 *
 * Props:
 *   onTaskCountChange(n)  — callback cập nhật badge count về component cha
 *   pollInterval (ms)     — tần suất tự refresh, mặc định 30000
 */
import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import AuthContext from '../../auth/Context'; // toàn cục — chỉnh lại nếu cấu trúc thư mục khác

import {
  Box, Stack, Typography, List, ListItem, ListItemIcon, ListItemText,
  ListItemButton, Button, Chip, Skeleton, Alert, Tooltip, Divider,
  IconButton, CircularProgress
} from '@mui/material';
import AssignmentIcon      from '@mui/icons-material/Assignment';
import RefreshIcon         from '@mui/icons-material/Refresh';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import AccessTimeIcon      from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon    from '@mui/icons-material/ErrorOutline';
import { useQuickAction } from '../../DataQuickActionContext'; 

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'vừa xong';
  if (m < 60) return `${m} phút trước`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} giờ trước`;
  return `${Math.floor(h / 24)} ngày trước`;
}

// ── Component ─────────────────────────────────────────────────────────────────
const UserformTaskPanel = ({ onTaskCountChange, pollInterval = 30000, filterNodeId = null }) => {
  const context = useContext(AuthContext);
  const {openAdd,openEdit} = useQuickAction();

  const [tasks,      setTasks]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);
  const pollRef = useRef(null);

  // ── Load tasks ──────────────────────────────────────────────────────────
  const loadTasks = useCallback(async (silent = false) => {
    const token     = context?.userInfo?.token;
    const userEmail = context?.userInfo?.email;
    if (!token || !userEmail) return;

    silent ? setRefreshing(true) : setLoading(true);
    setError(null);

    try {
      // filterNodeId: lọc theo node_id cụ thể (dùng khi panel mở từ node trên canvas)
      // null: hiển thị tất cả task thuộc user (dùng cho Task Panel toàn cục trong Drawer)
      const condition = filterNodeId
        ? { status: 'pending', node_id: filterNodeId }
        : { status: 'pending'};

      const res = await context.apis.asyncGetList(token, 'flowpending', { condition });
      const data = Array.isArray(res) ? res : (res?.data || []);
      setTasks(data);
      onTaskCountChange?.(data.length);
    } catch (e) {
      setError('Không thể tải danh sách yêu cầu nhập liệu.');
      console.error('[UserformTaskPanel]', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [context, onTaskCountChange]);

  // Mount + poll
  useEffect(() => {
    loadTasks();
    pollRef.current = setInterval(() => loadTasks(true), pollInterval);
    return () => clearInterval(pollRef.current);
  }, [loadTasks, pollInterval]);

  // ── Mở form khi click task ──────────────────────────────────────────────
  const handleOpenTask = useCallback((task) => {
    const token = context?.userInfo?.token;

    const onSuccess = async (savedDoc) => {
      try {
        await context.apis.asyncPostList(token, 'flowpending', {
          _id:            task._id,
          status:         'completed',
          result:         savedDoc,
          date_completed: new Date().toISOString()
        });
        // Xoá khỏi danh sách
        setTasks(prev => {
          const updated = prev.filter(t => t._id !== task._id);
          onTaskCountChange?.(updated.length);
          return updated;
        });
      } catch (e) {
        console.error('[UserformTaskPanel] update error:', e);
        setError('Không thể lưu kết quả. Vui lòng thử lại.');
      }
    };

   

    const effectiveMode = task.mode === 'auto'
      ? (task.prefill_data?._id ? 'edit' : 'create')
      : (task.mode || 'create');

    if (effectiveMode === 'edit' && task.prefill_data?._id) {
      openEdit(task.code,null,null, task.prefill_data, onSuccess);
    } else {
      openAdd(task.code,null,null, task.prefill_data, onSuccess);
    }
  }, [context, onTaskCountChange]);

  // ── Skeleton loading ────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 1 }}>
        {[1, 2, 3].map(i => (
          <Box key={i} sx={{ mb: 1 }}>
            <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 1 }} />
          </Box>
        ))}
      </Box>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Header: tổng số + nút refresh */}
      <Stack
        direction="row" alignItems="center" spacing={1}
        sx={{ px: 1, py: 0.75 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {tasks.length > 0
            ? `${tasks.length} yêu cầu đang chờ`
            : 'Không có yêu cầu nào'}
        </Typography>
        <Tooltip title="Làm mới">
          <IconButton size="small" onClick={() => loadTasks(true)} disabled={refreshing}>
            {refreshing
              ? <CircularProgress size={16} />
              : <RefreshIcon fontSize="small" />
            }
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider />

      {/* Lỗi */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          icon={<ErrorOutlineIcon fontSize="small" />}
          sx={{ mx: 1, mt: 1, py: 0.5, fontSize: 12 }}
        >
          {error}
        </Alert>
      )}

      {/* Danh sách rỗng */}
      {tasks.length === 0 && !error && (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, color: 'text.disabled' }}>
          <CheckCircleIcon sx={{ fontSize: 40, color: 'success.light' }} />
          <Typography variant="body2">Không có yêu cầu nhập liệu nào đang chờ</Typography>
        </Stack>
      )}

      {/* Danh sách task */}
      {tasks.length > 0 && (
        <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
          {tasks.map((task, idx) => {
        
            return (
              <React.Fragment key={task._id}>
                {idx > 0 && <Divider component="li" />}
                <ListItem
                  disablePadding
                  secondaryAction={
                    <Button
                      size="small"
                      variant={'contained'}
                      color="primary"
                      onClick={(e) => { e.stopPropagation(); handleOpenTask(task); }}
                      sx={{ textTransform: 'none', minWidth: 72, mr: 0.5 }}
                    >
                      Mở
                    </Button>
                  }
                >
                  <ListItemButton
                    onClick={() => handleOpenTask(task)}
                    
                    sx={{
                      pr: 10, // nhường chỗ cho nút Mở
                      borderLeft: '3px solid transparent',
                      borderColor: 'transparent',
                      transition: 'border-color 0.2s',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AssignmentIcon
                        fontSize="small"
                        color={'action'}
                      />
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {task.form_title || task.nodeName || 'Nhập liệu'}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 0.25 }}>
                          {task.flow_title && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>
                              {task.flow_title}
                            </Typography>
                          )}
                          <Chip
                            icon={<AccessTimeIcon sx={{ fontSize: '10px !important' }} />}
                            label={timeAgo(task.date_created)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
                          />
                          {task.mode && task.mode !== 'auto' && (
                            <Chip
                              label={task.mode === 'edit' ? 'Sửa' : 'Tạo mới'}
                              size="small"
                              color={task.mode === 'edit' ? 'info' : 'success'}
                              sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
                            />
                          )}
                        </Stack>
                      }
                      disableTypography
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default UserformTaskPanel;